// src/pages/Consultation.jsx
import React from 'react'
import { useNavigate } from 'react-router-dom'
import Hero from '../components/Hero'
import Card3D from '../components/Card3D'
import Carousel from '../components/Carousel'

const Consultation = () => {
  const navigate = useNavigate()

  const consultationServices = [
    {
      title: 'Strategic Property Advisory',
      description: 'Expert guidance on property investment, development, and portfolio optimization strategies.',
      icon: '🎯',
      features: [
        'Investment feasibility studies',
        'Portfolio optimization strategies',
        'Risk assessment and mitigation',
        'Market entry strategies'
      ]
    },
    {
      title: 'Market Research & Analysis',
      description: 'Comprehensive market studies and competitive analysis for informed decision-making.',
      icon: '📊',
      features: [
        'Market trend analysis',
        'Competitive benchmarking',
        'Supply-demand analysis',
        'Pricing strategy development'
      ]
    },
    {
      title: 'Development Consulting',
      description: 'End-to-end advisory for property development projects from conception to completion.',
      icon: '🏗️',
      features: [
        'Feasibility studies',
        'Project planning and management',
        'Cost-benefit analysis',
        'Development strategy'
      ]
    },
    {
      title: 'Investment Advisory',
      description: 'Data-driven insights for property investment decisions and portfolio management.',
      icon: '💼',
      features: [
        'Investment opportunity analysis',
        'ROI projections',
        'Risk assessment',
        'Portfolio diversification'
      ]
    },
    {
      title: 'Regulatory Compliance',
      description: 'Guidance on property laws, zoning regulations, and compliance requirements.',
      icon: '⚖️',
      features: [
        'Regulatory compliance review',
        'Zoning and land use analysis',
        'Permit acquisition support',
        'Legal documentation review'
      ]
    },
    {
      title: 'Sustainability Consulting',
      description: 'Environmental and sustainability advisory for green building and development.',
      icon: '🌱',
      features: [
        'Green building certification',
        'Environmental impact assessment',
        'Sustainable design advisory',
        'Energy efficiency optimization'
      ]
    }
  ]

  const processSteps = [
    {
      step: 1,
      title: 'Initial Consultation',
      description: 'Understanding your needs, objectives, and project scope through detailed discussions.',
      duration: '1-2 days'
    },
    {
      step: 2,
      title: 'Data Collection & Analysis',
      description: 'Gathering market data, property information, and conducting preliminary analysis.',
      duration: '3-5 days'
    },
    {
      step: 3,
      title: 'Strategy Development',
      description: 'Formulating customized strategies and recommendations based on comprehensive analysis.',
      duration: '2-4 days'
    },
    {
      step: 4,
      title: 'Implementation Support',
      description: 'Assisting with strategy implementation and providing ongoing advisory support.',
      duration: 'Ongoing'
    }
  ]

  const caseStudies = [
    {
      title: 'Commercial Portfolio Optimization',
      client: 'Prime Properties Ltd',
      challenge: 'Underperforming commercial portfolio in Accra CBD',
      solution: 'Strategic repositioning and tenant mix optimization',
      results: '25% increase in rental income within 6 months'
    },
    {
      title: 'Market Entry Strategy',
      client: 'International Developer',
      challenge: 'Entering the Nigerian residential market',
      solution: 'Comprehensive market analysis and entry strategy',
      results: 'Successful launch of 200-unit development'
    },
    {
      title: 'Sustainable Development Advisory',
      client: 'GreenBuild Africa',
      challenge: 'Achieving green building certification',
      solution: 'Sustainability integration and certification guidance',
      results: 'LEED Gold certification achieved'
    }
  ]

  return (
    <div>
      <Hero 
        title="OFANDA CONSULTATION"
        subtitle="Strategic real estate advisory and market analysis services for informed decision-making"
      />

      {/* Overview Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 font-playfair mb-6">
                Expert Consultation Services
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Our consultation services provide strategic insights and expert guidance to help you 
                make informed real estate decisions across African markets. We combine deep local 
                expertise with international best practices to deliver comprehensive solutions.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                Whether you're an investor, developer, or property owner, our advisory services 
                help you navigate complex market dynamics and maximize your returns.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/contact')}
                  className="bg-ofanda-yellow text-gray-900 px-8 py-3 rounded-lg font-semibold hover:bg-yellow-600 transition-colors duration-300"
                >
                  Start Your Consultation
                </button>
                <button className="border-2 border-ofanda-yellow text-ofanda-yellow px-8 py-3 rounded-lg font-semibold hover:bg-ofanda-yellow hover:text-gray-900 transition-colors duration-300">
                  Download Brochure
                </button>
              </div>
            </div>
            <div className="bg-gray-100 rounded-2xl p-8">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Our Expertise</h3>
              <div className="space-y-3">
                {[
                  '15+ years African market experience',
                  'RICS certified professionals',
                  'Data-driven insights',
                  'Local market intelligence',
                  'International standards compliance'
                ].map((expertise, index) => (
                  <div key={index} className="flex items-center">
                    <svg className="w-5 h-5 text-ofanda-yellow mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">{expertise}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 font-playfair mb-4">
              Our Consultation Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive advisory solutions tailored to your specific needs and objectives
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {consultationServices.map((service, index) => (
              <Card3D key={index}>
                <div className="text-4xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-gray-700">
                      <svg className="w-4 h-4 text-ofanda-yellow mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </Card3D>
            ))}
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 font-playfair mb-4">
              Our Consultation Process
            </h2>
            <p className="text-xl text-gray-600">
              Structured approach to delivering actionable insights and strategies
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-ofanda-yellow h-full hidden lg:block"></div>
            
            <div className="space-y-12">
              {processSteps.map((step, index) => (
                <div key={step.step} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'} lg:flex-row`}>
                  <div className="w-full lg:w-1/2"></div>
                  <div className="w-12 h-12 bg-ofanda-yellow rounded-full flex items-center justify-center text-white font-bold text-lg z-10 mx-4 flex-shrink-0">
                    {step.step}
                  </div>
                  <div className="w-full lg:w-1/2">
                    <Card3D>
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-xl font-semibold text-gray-900">{step.title}</h3>
                        <span className="bg-ofanda-yellow bg-opacity-20 text-ofanda-yellow px-3 py-1 rounded-full text-sm font-semibold">
                          {step.duration}
                        </span>
                      </div>
                      <p className="text-gray-600">{step.description}</p>
                    </Card3D>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>



      {/* CTA Section */}
      <section className="py-20 bg-ofanda-yellow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 font-playfair mb-4">
            Ready to Transform Your Real Estate Strategy?
          </h2>
          <p className="text-xl text-gray-800 mb-8">
            Contact us today for a personalized consultation and discover how our expertise can drive your success.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/contact')}
              className="bg-gray-900 text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors duration-300"
            >
              Schedule Consultation
            </button>
            <button className="bg-white text-gray-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300">
              Download Service Catalog
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Consultation